import { Icon } from '@iconify/react';
import { Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useContext, useState } from "react";
import toast from "react-hot-toast";
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog';
import CardActions from "src/pages/sistema/components/CardActions";
import { EntityWithIdFields, QueryStatus } from "src/types";
import { HttpLEP } from "../api/httpLEP";
import { useEmisorLimite } from "../hooks/useEmisorLimite";
import { OperacionLEPEmisorFields, OperacionLEPFields } from "../types/fields";
import { OperacionLEP } from "../types/types";
import { DialogAgregarEmisor } from "./DialogAgregarEmisor";
import { OperacionLepEmisorFila } from './OperacionLepEmisorFila';
import { ContextOperacionType, ContextOperacion } from 'src/pages/sistema/RefactorLayout';
import { PermissionType } from 'src/types/security';

interface OperacionLEPLimitesTablaProps {
    operacion: OperacionLEP;
    codSistema: number;
}

function OperacionLepLimitesTabla({ operacion, codSistema }: OperacionLEPLimitesTablaProps) {
    const idOperacion = operacion[EntityWithIdFields.Id];

    const { permisoTipo } = useContext<ContextOperacionType>(ContextOperacion);
    const permiteAgregarEmisores = permisoTipo >= PermissionType.Write;
    const permiteEditarEmisores = permisoTipo >= PermissionType.Write;
    const permiteEliminarEmisores = permisoTipo >= PermissionType.Write;

    const { limites, status, refetch } = useEmisorLimite(idOperacion);
    const [openAddDialog, setOpenAddDialog] = useState(false);

    const { askConfirmation } = useConfirmationDialog();

    const handleDelete = async (idEmisor: number) => {
        try {
            await HttpLEP.deleteEmisor(idOperacion, idEmisor);
            toast.success('Emisor eliminado correctamente');
            refetch();
        } catch (error) {
            toast.error('Error al eliminar el emisor');
        }
    };

    const confirmDelete = (id: number) => {
        askConfirmation(
            () => handleDelete(id),
            "¿Está seguro de que desea eliminar este emisor?"
        );
    };

    return (
        <CardActions
            title='Emisores'
            icon='mdi:account-cash-outline'
            tableContent
            actions={
                permiteAgregarEmisores ? (
                    <Button
                        size='small'
                        variant='contained'
                        startIcon={<Icon icon="tabler:plus" />}
                        onClick={() => setOpenAddDialog(true)}
                    >
                        Agregar
                    </Button>
                ) : <></>
            }
        >
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell width="45%">Razón Social</TableCell>
                            <TableCell width="45%">Tipo Emisor</TableCell>
                           {
                            (permiteEliminarEmisores && limites?.filter(l => l[OperacionLEPEmisorFields.PermiteEditar])?.length || 0 > 0)
                            &&
                            <TableCell align="right">Acciones</TableCell>
                           }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {status === QueryStatus.Loading && (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        )}

                        {status === QueryStatus.Success && limites?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        No hay emisores registrados
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}

                        {status === QueryStatus.Success && limites?.map((emisor) => (
                            <OperacionLepEmisorFila
                                key={emisor.id}
                                operacionId={idOperacion}
                                codSistema={codSistema}
                                emisor={emisor}
                                refetch={refetch}
                                onDelete={(permiteEliminarEmisores && emisor[OperacionLEPEmisorFields.PermiteEditar]) ? confirmDelete : undefined}
                                permiteEditar={permiteEditarEmisores && emisor[OperacionLEPEmisorFields.PermiteEditar]}
                            />
                        ))}

                        {status === QueryStatus.Error && (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'error.main' }}>
                                    Error al cargar emisores
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <DialogAgregarEmisor
                open={openAddDialog}
                onClose={() => setOpenAddDialog(false)}
                onSuccess={() => refetch()}
            />
        </CardActions>
    );
}

export default OperacionLepLimitesTabla;
